import { IViewPort } from 'interfaces/IViewPort';
import { IScrollData } from 'interfaces/IScrollData';
import { sessionStore } from 'store/Session';
import { PIXEL_ROUNDING_OFFSET } from 'constants/constants';
import { isScrollbarAvailable } from './utils/isScrollbarAvailable';
import { HeightManager } from './utils/heightManager';
import { SupportMethodsManager } from './utils/SupportMethodsManager';
import { asyncRepeat } from './utils/asyncRepeat';
import { isInIframe } from './utils/isInIframe';
import { EventTypes } from './enums/EventTypes';
import { IIFrameIntegration, IPMEventResponse } from './interfaces';
import { postMessenger } from './postMessenger';
import { UPDATE_HEIGHT_REPEAT } from './constants/constants';

let latestHeight: number;
const heightManager = new HeightManager();
const supportMethodsManager = new SupportMethodsManager();

export const IframeIntegration: IIFrameIntegration = {
    realityCheck: () => {
        if (!isInIframe()) {
            return;
        }

        postMessenger.emit({
            type: EventTypes.REALITY_CHECK,
        });
    },

    balanceCheck: () => {
        if (!isInIframe()) {
            return;
        }

        postMessenger.emit({
            type: EventTypes.BALANCE_CHECK,
        });
    },

    scrollToTop: (topOffset = 0) => {
        if (isInIframe() && !isScrollbarAvailable() && sessionStore.dynamicHeight) {
            postMessenger.emit({
                type: EventTypes.IFRAME_TOP,
                payload: {
                    topOffset,
                },
            });
        } else {
            window.scroll(0, topOffset);
        }
    },

    scrollToPosition: (position: number) => {
        if (isInIframe() && !isScrollbarAvailable()) {
            postMessenger.emit({
                type: EventTypes.SCROLL_TO,
                payload: {
                    scrollTo: position,
                },
            });
        } else {
            window.scroll(0, position);
        }
    },

    getViewPort: () =>
        new Promise((resolve) => {
            const defaults = {
                width: window.innerWidth,
                height: Math.max(window.innerHeight, 500),
                iFrameTopOffset: 0,
                scrollY: 0,
            };

            if (!isInIframe()) {
                resolve(defaults);
            }

            postMessenger
                .get<IViewPort>(EventTypes.VIEW_PORT)
                .then((payload) => resolve(payload))
                .catch(() => defaults);
        }),

    sendNewHeight: (value?: number): void => {
        const height = value || document.body.offsetHeight + PIXEL_ROUNDING_OFFSET; // 1 is needed for pixel rounding
        const newHeight = Math.max(Number(height), 500);
        latestHeight = newHeight;

        postMessenger.emit({
            type: EventTypes.NEW_HEIGHT,
            payload: {
                height: newHeight,
            },
        });
    },

    updateHeight: (height: number = undefined) => {
        if (!isInIframe() || !sessionStore.dynamicHeight) {
            return;
        }

        IframeIntegration.sendNewHeight(height);

        latestHeight = height;

        asyncRepeat(
            () =>
                new Promise((resolve) => {
                    window.setTimeout(() => {
                        IframeIntegration.sendNewHeight(latestHeight);
                        resolve();
                    }, 300);
                }),
            UPDATE_HEIGHT_REPEAT,
        );
    },

    subscribeScroll: (callback): ICallback => {
        if (!isInIframe()) {
            return null;
        }

        postMessenger.emit({ type: EventTypes.SUBSCRIBE_SCROLL });

        return postMessenger.subscribe(
            EventTypes.ON_SCROLL,
            (data: IPMEventResponse<IScrollData>) => {
                supportMethodsManager.add(EventTypes.ON_SCROLL);
                return callback(data.payload);
            },
        );
    },

    unsubscribeScroll: () => {
        if (!isInIframe()) {
            return;
        }
        postMessenger.emit({ type: EventTypes.UNSUBSCRIBE_SCROLL });
    },

    getHeightManager: () => heightManager,

    getSupportedMethodManager: () => supportMethodsManager,
};
